import {
  LoanCaseOfferStateEnum,
  SetNextStepRq,
} from "@smartx/mikro-vt-client-api"
import { useContext } from "react"
import { useNavigate } from "react-router"
import { offerSetNextStepApi } from "../api/apiClient"
import { useAppDispatch, useOfferData } from "../app/hooks"
import { setBeError } from "../app/slices/beErrorSlice"
import { getOfferData } from "../app/slices/offerDataSlice"
import { STEP_MATRIX } from "../modules/Offer/OfferStepper"
import AuthContext from "../providers/AuthProvider"
import { RequestHeader } from "../utils/utils"

interface INextStepConfig {
  offerState: LoanCaseOfferStateEnum
  toNextStep: boolean
  closeFromRibbon: boolean
}

const NEXT_STEP_MATRIX: INextStepConfig[] = [
  { offerState: "EVALUATION_FAILED", toNextStep: false, closeFromRibbon: true },
  { offerState: "EVALUATION_ERROR", toNextStep: true, closeFromRibbon: false },
  {
    offerState: "EVALUATION_SUCCESS",
    toNextStep: true,
    closeFromRibbon: false,
  },
  {
    offerState: "EVALUATION_MODIFICATION",
    toNextStep: true,
    closeFromRibbon: false,
  },
  { offerState: "SUMMARY", toNextStep: true, closeFromRibbon: false },
  { offerState: "DOCUMENTS", toNextStep: true, closeFromRibbon: false },
]

export const useOfferScreenFlow = () => {
  const authContext = useContext(AuthContext)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const offerData = useOfferData()

  const goToNextOfferScreen = async (
    caseId: string,
    sourceId?: string,
    keepHistory?: boolean,
  ): Promise<boolean> => {
    if (!caseId && !sourceId) {
      return false
    }

    authContext.refresh()

    const result = await dispatch(
      getOfferData({
        header: new RequestHeader(),
        sourceId,
        caseId,
      }),
    )

    if (getOfferData.fulfilled.match(result)) {
      const offerState = result.payload.loanCase?.offerState
      const screen = STEP_MATRIX.find((el) =>
        el.offerStates?.includes(offerState),
      )

      if (screen) {
        navigate("/offer/" + screen.route, { replace: !keepHistory })
        return true
      }
    }

    return false
  }

  const setNextStep = async (caseId: string, fromRibbon?: boolean) => {
    const step = NEXT_STEP_MATRIX.find(
      (el) => el.offerState === offerData.offerState,
    )
    const navToDashboard =
      !offerData?.caseId ||
      offerData?.caseId !== caseId ||
      !step ||
      (fromRibbon && !step?.closeFromRibbon)

    if (navToDashboard) {
      navigate("/dashboard", { replace: true })
      return
    }

    authContext.refresh()

    const rq: SetNextStepRq = {
      header: new RequestHeader(),
      caseId,
      actualScreen: offerData?.offerState,
    }

    try {
      const result = await offerSetNextStepApi.setNextStep(rq)
      if (!result?.data?.success) {
        throw new Error("Offer error")
      }

      if (step?.toNextStep) {
        goToNextOfferScreen(offerData?.caseId)
      } else {
        navigate("/dashboard", { replace: true })
      }
    } catch {
      dispatch(setBeError(true))
    }
  }

  return {
    goToNextOfferScreen,
    setNextStep,
  }
}
